// Growem — AI shim. Calls the same-origin Pages Functions and always degrades gracefully:
// meal analysis falls back to the offline FOODS heuristic; the coach returns null so the
// caller uses its rule-based reply bank.

import { supabase } from './supabase'
import { estimateLocal } from '../domain/nutrition'
import type { MealEstimate } from '../types'

async function authHeaders(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token
  return {
    'content-type': 'application/json',
    ...(token ? { authorization: `Bearer ${token}` } : {}),
  }
}

export async function analyzeMeal(description: string, imageDataUrl?: string): Promise<MealEstimate> {
  try {
    const res = await fetch('/api/analyze-meal', {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ description, imageDataUrl }),
    })
    const data = await res.json().catch(() => ({ error: 'bad response' }))
    if (!res.ok || data?.error) throw new Error(data?.error ?? `http ${res.status}`)
    return {
      nombre: String(data.nombre ?? description.slice(0, 40) ?? 'Comida'),
      kcal: Math.round(Number(data.kcal) || 0),
      prot: Math.round(Number(data.prot) || 0),
      carbs: Math.round(Number(data.carbs) || 0),
      fat: Math.round(Number(data.fat) || 0),
      note: String(data.note ?? ''),
      source: 'ai_openai',
    }
  } catch {
    return estimateLocal(description)
  }
}

export interface CoachContext {
  streak: number
  best_streak: number
  session: { name?: string; doneN: number; totN: number; trainAt?: string | null } | null
  protein_g: number
  protein_min: number
  protein_max: number
  kcal: number
  kcal_min: number
  water_l: number
  water_min: number
  water_max: number
  creatine: boolean
  hour_local: number
  flags: string[]
}

/** Returns the coach reply, or null when the AI is unavailable (caller uses growyReply). */
export async function askCoach(
  message: string,
  context: CoachContext,
  history: Array<{ role: 'user' | 'coach'; text: string }>,
): Promise<string | null> {
  try {
    const res = await fetch('/api/coach', {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ message, context, history }),
    })
    const data = await res.json().catch(() => ({ error: 'bad response' }))
    if (!res.ok || data?.error) return null
    const reply = String(data.reply ?? '').trim()
    return reply || null
  } catch {
    return null
  }
}
